import { useEffect } from 'react'
import { useConnectionStore } from '@/stores/connectionStore'
import { useDataSourceQuery } from '@/hooks/useDataSources'
import { toConnectionType } from '@/utils/databaseCapabilities'

/**
 * Hook to automatically restore the last active connection on app load
 */
export function useLastConnection() {
  const {
    lastActiveConnectionId,
    activeConnection,
    setActiveConnection
  } = useConnectionStore()

  // Only query the datasource if we have a lastActiveConnectionId but no activeConnection
  const shouldFetch = !!lastActiveConnectionId && !activeConnection

  const {
    data: dataSource,
    isLoading,
    error
  } = useDataSourceQuery(shouldFetch ? lastActiveConnectionId : '')

  useEffect(() => {
    // If we have a lastActiveConnectionId but no activeConnection,
    // And we successfully fetched the datasource data, restore the connection
    if (lastActiveConnectionId && !activeConnection && dataSource) {
      // Use centralized utility to map databaseType to connection type
      const mappedType = toConnectionType(dataSource.databaseType)

      // Get default port based on database type
      const getDefaultPort = (type: typeof mappedType): number => {
        switch (type) {
          case 'mysql': return 3306
          case 'redis': return 6379
          case 'redis-cluster': return 6379
          case 'memcached': return 11211
          case 'oracle': return 1521
          case 'mongodb': return 27017
          case 'postgresql': return 5432
          case 'clickhouse': return 8123
          case 'scylladb': return 9042
          case 'milvus': return 19530
          default: return 5432
        }
      }

      const connectionData = {
        id: String(dataSource.id),
        name: dataSource.displayName || `${dataSource.host}:${dataSource.port}`,
        type: mappedType,
        host: dataSource.host || '',
        port: dataSource.port || getDefaultPort(mappedType),
        username: dataSource.username || '',
        // We don't store passwords in the UI store
        password: '',
        database: dataSource.defaultDatabase || undefined,
        createdAt: dataSource.createdAt || new Date().toISOString(),
        updatedAt: dataSource.updatedAt || new Date().toISOString(),
      }

      console.log(`[useLastConnection] Restoring connection:`, {
        dbType: dataSource.databaseType,
        mappedType,
        connectionId: connectionData.id
      })

      setActiveConnection(connectionData)
    }
  }, [lastActiveConnectionId, activeConnection, dataSource, isLoading, setActiveConnection])

  useEffect(() => {
    // Handle error case - if we can't fetch the last connection, clear it
    if (error && lastActiveConnectionId && !activeConnection) {
      console.warn('Failed to restore last connection:', error)
      // Don't show error toast on app load, just silently clear
    }
  }, [error, lastActiveConnectionId, activeConnection])

  return {
    isRestoring: isLoading && !!lastActiveConnectionId && !activeConnection,
    restoredConnection: activeConnection,
    restorationError: error
  }
}
